import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { P } from "../../styles/tokens";

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: P.bg,
    padding: 24,
  },
  card: {
    background: P.surface,
    borderRadius: 24,
    padding: "40px 32px",
    maxWidth: 400,
    width: "100%",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  },
  logo: {
    textAlign: "center",
    fontSize: 48,
    marginBottom: 8,
  },
  title: {
    textAlign: "center",
    fontSize: 24,
    fontWeight: 700,
    color: P.text,
    marginBottom: 4,
    fontFamily: "'Outfit', sans-serif",
  },
  subtitle: {
    textAlign: "center",
    fontSize: 14,
    color: P.textSm,
    marginBottom: 28,
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    borderRadius: 12,
    border: `1.5px solid ${P.border}`,
    fontSize: 15,
    fontFamily: "'Outfit', sans-serif",
    outline: "none",
    marginBottom: 12,
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  btn: {
    width: "100%",
    padding: "12px 0",
    borderRadius: 12,
    border: "none",
    fontSize: 15,
    fontWeight: 600,
    fontFamily: "'Outfit', sans-serif",
    cursor: "pointer",
    transition: "opacity 0.2s",
  },
  primaryBtn: {
    background: P.ingresos.base,
    color: "#fff",
    marginBottom: 12,
  },
  googleBtn: {
    background: "#fff",
    color: P.text,
    border: `1.5px solid ${P.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginBottom: 16,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "20px 0",
    color: P.textXs,
    fontSize: 13,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: P.border,
  },
  toggle: {
    textAlign: "center",
    fontSize: 14,
    color: P.textSm,
  },
  toggleLink: {
    color: P.ingresos.base,
    fontWeight: 600,
    cursor: "pointer",
    background: "none",
    border: "none",
    fontSize: 14,
    fontFamily: "'Outfit', sans-serif",
  },
  error: {
    background: "#FEF2F2",
    color: "#DC2626",
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: 13,
    marginBottom: 16,
    textAlign: "center",
  },
  info: {
    background: "#F0F9FF",
    color: "#0369A1",
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: 13,
    marginBottom: 16,
    textAlign: "center",
  },
};

export default function LoginPage() {
  const { signInWithEmail, signUpWithEmail, signInWithGoogle } = useAuth();
  const [mode, setMode] = useState("login"); // 'login' | 'signup'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);

    try {
      if (mode === "login") {
        await signInWithEmail(email, password);
      } else {
        await signUpWithEmail(email, password);
        setInfo(
          "Revisa tu correo para confirmar tu cuenta. Puede tardar unos minutos.",
        );
      }
    } catch (err) {
      setError(err.message || "Error de autenticación");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err.message || "Error con Google");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>🐿️</div>
        <div style={styles.title}>Arbo</div>
        <div style={styles.subtitle}>
          {mode === "login"
            ? "Inicia sesión en tu cuenta"
            : "Crea tu cuenta gratis"}
        </div>

        {error && <div role="alert" style={styles.error}>{error}</div>}
        {info && <div role="alert" style={styles.info}>{info}</div>}

        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
            autoComplete="email"
          />
          <input
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            required
            minLength={6}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
          />
          <button
            type="submit"
            disabled={loading}
            style={{ ...styles.btn, ...styles.primaryBtn, opacity: loading ? 0.6 : 1 }}
          >
            {loading
              ? "Cargando..."
              : mode === "login"
                ? "Iniciar sesión"
                : "Crear cuenta"}
          </button>
        </form>

        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span>o</span>
          <span style={styles.dividerLine} />
        </div>

        <button
          onClick={handleGoogle}
          style={{ ...styles.btn, ...styles.googleBtn }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path
              d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
              fill="#4285F4"
            />
            <path
              d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
              fill="#34A853"
            />
            <path
              d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
              fill="#FBBC05"
            />
            <path
              d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
              fill="#EA4335"
            />
          </svg>
          Continuar con Google
        </button>

        <div style={styles.toggle}>
          {mode === "login" ? "¿No tienes cuenta? " : "¿Ya tienes cuenta? "}
          <button
            style={styles.toggleLink}
            onClick={() => {
              setMode(mode === "login" ? "signup" : "login");
              setError("");
              setInfo("");
            }}
          >
            {mode === "login" ? "Regístrate" : "Inicia sesión"}
          </button>
        </div>
      </div>
    </div>
  );
}
